import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AlertCircle, Wifi, WifiOff, Copy, Check, Download } from 'lucide-react';

interface WebRTCControllerClientProps {
  className?: string;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';
type RTCState = 'idle' | 'offer-received' | 'answer-sent' | 'connected';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'error' | 'success' | 'debug';
  message: string;
}

export const WebRTCControllerClient: React.FC<WebRTCControllerClientProps> = ({ className }) => {
  const [roomId, setRoomId] = useState('default-session');
  const [userRole, setUserRole] = useState<'admin' | 'operator' | 'viewer'>('operator');
  const [targetPeerId, setTargetPeerId] = useState<string | null>(null);
  const [ownPeerId, setOwnPeerId] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [rtcState, setRtcState] = useState<RTCState>('idle');
  const [error, setError] = useState<string>('');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    offerReceived: false,
    answerSent: false,
    iceCandidatesSent: 0,
    iceCandidatesReceived: 0,
    dataChanelOpen: false,
    connectionStatus: 'Idle',
    messagesSent: 0,
    messagesReceived: 0,
    lastHeartbeat: 0,
  });
  const [heartbeatActive, setHeartbeatActive] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const answerSentRef = useRef(false);
  const bufferedIceCandidatesRef = useRef<any[]>([]);
  const offerCountRef = useRef(0);
  const answerCountRef = useRef(0);
  const iceCandidatesSentCountRef = useRef(0);
  const currentOfferIdRef = useRef<string | null>(null);
  const offerFromPeerIdRef = useRef<string | null>(null);

  const addLog = (message: string, level: 'info' | 'error' | 'success' | 'debug' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, level, message }]);
  };

  // No auto-scroll. User scrolls manually if needed.

  const handleOffer = async (offerSdp: string, offerId?: string, fromPeerId?: string) => {
    if (!pcRef.current) return;

    try {
      offerCountRef.current++;
      const offerNum = offerCountRef.current;
      const tStart = performance.now();

      // Store offerId to echo back in answer
      currentOfferIdRef.current = offerId || null;
      // Store who sent the offer (for routing the answer)
      offerFromPeerIdRef.current = fromPeerId || null;

      addLog(`[Offer #${offerNum}] Received (${offerSdp.length} bytes, first 40 chars: ${offerSdp.substring(0, 40)})${offerId ? ` [offerId: ${offerId}]` : ''}`, 'debug');
      setStats((prev) => ({ ...prev, offerReceived: true }));
      setRtcState('offer-received');

      // Step 1: Set remote description
      const tSRD = performance.now();
      await pcRef.current.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: offerSdp }));
      addLog(`[Offer #${offerNum}] setRemoteDescription completed (${(performance.now() - tSRD).toFixed(1)}ms)`, 'success');

      // Diagnostic: Wait a moment for ondatachannel to fire
      await new Promise(r => setTimeout(r, 50));
      addLog(`[Offer #${offerNum}] After SRD wait: dcRef.current is ${dcRef.current ? `populated (label: ${dcRef.current.label})` : 'null'}`, 'debug');

      // Step 1.5: Ensure we have a transceiver to receive video (for renegotiations like start_stream)
      // This safely ensures we can receive video tracks when Vision adds them
      const videoTransceivers = pcRef.current.getTransceivers().filter(t => t.mid === '1' || t.receiver?.track?.kind === 'video');
      if (videoTransceivers.length === 0 && offerSdp.includes('m=video')) {
        try {
          pcRef.current.addTransceiver('video', { direction: 'recvonly' });
          addLog(`[Offer #${offerNum}] Added recvonly video transceiver`, 'debug');
        } catch (e) {
          addLog(`[Offer #${offerNum}] Could not add video transceiver: ${e}`, 'debug');
        }
      }

      // Step 2: Create answer
      const tCreateAnswer = performance.now();
      const answer = await pcRef.current.createAnswer();
      addLog(`[Offer #${offerNum}] createAnswer completed (${(performance.now() - tCreateAnswer).toFixed(1)}ms)`, 'info');

      // Step 3: Set local description
      const tSLD = performance.now();
      await pcRef.current.setLocalDescription(answer);
      addLog(`[Offer #${offerNum}] setLocalDescription completed (${(performance.now() - tSLD).toFixed(1)}ms)`, 'success');

      // Step 4: Send answer via WebSocket (IMMEDIATELY, no delay)
      const answerSdp = pcRef.current.localDescription?.sdp;
      if (!answerSdp) {
        throw new Error('Failed to get local description SDP');
      }

      addLog(`[Offer #${offerNum}] Answer ready (${answerSdp.length} bytes, first 40 chars: ${answerSdp.substring(0, 40)})`, 'debug');

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const answerMsg: any = {
          type: 'answer',
          data: {
            sessionId: roomId,
            from: ownPeerId || 'unknown',
            to: offerFromPeerIdRef.current || 'unknown',
            sdp: answerSdp,
          }
        };
        // Echo offerId back if Vision sent one
        if (currentOfferIdRef.current) {
          answerMsg.data.offerId = currentOfferIdRef.current;
        }
        wsRef.current.send(JSON.stringify(answerMsg));
        answerCountRef.current++;
        answerSentRef.current = true;
        const totalTime = performance.now() - tStart;
        addLog(`[Offer #${offerNum}] Answer sent via WebSocket (total time: ${totalTime.toFixed(1)}ms)${currentOfferIdRef.current ? ` [offerId: ${currentOfferIdRef.current}]` : ''}`, 'success');
        setStats((prev) => ({ ...prev, answerSent: true }));
        setRtcState('answer-sent');
      } else {
        throw new Error('WebSocket not open');
      }

      // Step 5: Apply buffered ICE candidates (after answer sent)
      if (bufferedIceCandidatesRef.current.length > 0) {
        addLog(`[Offer #${offerNum}] Applying ${bufferedIceCandidatesRef.current.length} buffered ICE candidates`, 'info');
        let successCount = 0;
        for (const candidate of bufferedIceCandidatesRef.current) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
            successCount++;
          } catch (e) {
            addLog(`[Offer #${offerNum}] Error adding buffered ICE: ${e}`, 'error');
          }
        }
        addLog(`[Offer #${offerNum}] Applied ${successCount}/${bufferedIceCandidatesRef.current.length} buffered ICE candidates`, 'info');
        bufferedIceCandidatesRef.current = [];
      }
    } catch (e) {
      addLog(`Error handling offer: ${e}`, 'error');
      setError(`Offer handling failed: ${e}`);
      setConnectionState('error');
    }
  };

  const handleIceCandidate = async (iceMsg: any) => {
    if (!pcRef.current) return;

    try {
      const candidate = iceMsg.candidate;
      if (!candidate) return;

      // Normalize candidate to RTCIceCandidateInit format
      let init: RTCIceCandidateInit;
      if (typeof candidate === 'string') {
        // Raw string format - provide defaults for sdpMid/sdpMLineIndex
        init = {
          candidate,
          sdpMid: '0',
          sdpMLineIndex: 0,
        };
      } else if (typeof candidate === 'object') {
        // Object format with candidate, sdpMid, sdpMLineIndex
        init = {
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid ?? '0',
          sdpMLineIndex: candidate.sdpMLineIndex ?? 0,
        };
      } else {
        addLog(`Invalid candidate format: ${typeof candidate}`, 'error');
        return;
      }

      // If we haven't set remote description yet, buffer the candidate
      if (pcRef.current.remoteDescription === null) {
        addLog(`Buffering ICE candidate (before SRD): ${init.candidate?.substring(0, 40) || 'null'}...`, 'debug');
        bufferedIceCandidatesRef.current.push(init);
        return;
      }

      await pcRef.current.addIceCandidate(new RTCIceCandidate(init));
      const newCount = stats.iceCandidatesReceived + 1;
      setStats((prev) => ({ ...prev, iceCandidatesReceived: newCount }));
      addLog(`ICE candidate added (total received: ${newCount})`, 'debug');
    } catch (e) {
      addLog(`Error adding ICE candidate: ${e}`, 'error');
    }
  };

  const handleWebSocketMessage = async (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data);
      const data = msg.data || msg; // Support both wrapped and unwrapped formats

      switch (msg.type) {
        case 'joined': {
          addLog(`Joined session: peers=${data.peers?.length || 0}, iceServers=${data.iceServers?.length || 0}`, 'success');
          // Use sessionId as own peerId (server will route based on this)
          setOwnPeerId(data.sessionId);
          // If there are existing peers, connect to the first one
          if (data.peers && data.peers.length > 0) {
            setTargetPeerId(data.peers[0]);
            addLog(`Found ${data.peers.length} existing peer(s). Will connect to ${data.peers[0]}`, 'info');
          }
          break;
        }

        case 'peer-joined': {
          addLog(`Peer joined: peerId=${data.peerId}, role=${data.role}`, 'info');
          break;
        }

        case 'peer-left': {
          addLog(`Peer left: peerId=${data.peerId}`, 'info');
          break;
        }

        case 'offer': {
          addLog(`Received offer from ${data.from}`, 'info');
          await handleOffer(data.sdp, data.offerId, data.from);
          break;
        }

        case 'ice': {
          addLog(`Received ICE candidate`, 'debug');
          await handleIceCandidate(data);
          break;
        }

        case 'ctrl-result': {
          addLog(`Control result: ${data.status}${data.message ? ` - ${data.message}` : ''}`, 'info');
          break;
        }

        case 'ping': {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'pong', data: { payload: data.payload } }));
            // Update heartbeat status without logging (avoid spam)
            setStats((prev) => ({ ...prev, lastHeartbeat: Date.now() }));
            setHeartbeatActive(true);
            setTimeout(() => setHeartbeatActive(false), 2000); // 2s for visible blink
          }
          break;
        }

        case 'error': {
          addLog(`Server error: ${data.message}`, 'error');
          setError(`Server error: ${data.message}`);
          break;
        }

        default: {
          addLog(`Unknown message type: ${msg.type}`, 'debug');
        }
      }
    } catch (e) {
      addLog(`Error parsing WebSocket message: ${e}`, 'error');
    }
  };

  const setupRTCConnection = () => {
    try {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      });

      // DO NOT create data channel on answerer (controller).
      // Only the offerer (Vision) creates it.
      // Controller will receive it via pc.ondatachannel below.

      // Setup data channel handlers
      const setupDataChannelHandlers = (channel: RTCDataChannel) => {
        console.log(`[WebRTC] Data channel setup: label="${channel.label}", readyState=${channel.readyState}`);
        addLog(`[DC:${channel.label}] Channel created with state: ${channel.readyState}`, 'debug');

        channel.onopen = () => {
          console.log(`[WebRTC] Data channel opened: ${channel.label}`);
          addLog(`[DC:${channel.label}] ✓ OPEN (ready for data)`, 'success');
          if (channel.label === 'control') {
            addLog(`[DC:control] Waiting for hello message...`, 'info');
          } else if (channel.label === 'frames') {
            addLog(`[DC:frames] Ready to receive mocap frames`, 'info');
          }
          setStats((prev) => ({ ...prev, dataChanelOpen: true }));
        };

        channel.onclose = () => {
          console.log(`[WebRTC] Data channel closed: ${channel.label}`);
          addLog(`[DC:${channel.label}] CLOSED`, 'info');
          setStats((prev) => ({ ...prev, dataChanelOpen: false }));
        };

        channel.onerror = (e) => {
          console.log(`[WebRTC] Data channel error [${channel.label}]:`, e);
          addLog(`[DC:${channel.label}] ERROR: ${e}`, 'error');
        };

        channel.onmessage = (e) => {
          try {
            // Handle control channel (JSON)
            if (channel.label === 'control') {
              let msg: any;
              if (typeof e.data === 'string') {
                msg = JSON.parse(e.data);
              } else if (e.data instanceof ArrayBuffer) {
                const decoder = new TextDecoder();
                const jsonStr = decoder.decode(e.data);
                msg = JSON.parse(jsonStr);
              } else {
                msg = e.data;
              }

              if (msg.schemaVersion) {
                addLog(
                  `[DC:control] ✓ HELLO received: schemaVersion=${msg.schemaVersion}, source=${msg.source?.type}, middleware=${msg.middleware}`,
                  'success'
                );
              } else if (typeof msg === 'object' && msg.type) {
                addLog(
                  `[DC:control] Message: ${msg.type}${msg.command ? ` (${msg.command})` : ''}`,
                  'info'
                );
              } else {
                addLog(`[DC:control] JSON: ${JSON.stringify(msg).substring(0, 100)}`, 'debug');
              }
            }
            // Handle frames channel (binary protobuf)
            else if (channel.label === 'frames') {
              if (e.data instanceof ArrayBuffer) {
                addLog(
                  `[DC:frames] ✓ Frame received: ${e.data.byteLength} bytes`,
                  'success'
                );
              } else {
                addLog(
                  `[DC:frames] Data: ${e.data?.byteLength || e.data?.length || '?'} bytes`,
                  'debug'
                );
              }
            } else {
              addLog(`[DC:${channel.label}] Message (${e.data?.byteLength || '?'} bytes)`, 'debug');
            }
          } catch (err) {
            addLog(`[DC:${channel.label}] Parse error: ${err}`, 'error');
          }
          setStats((prev) => ({ ...prev, messagesReceived: prev.messagesReceived + 1 }));
        };
      };

      // Handle incoming data channel from Vision peer (offerer)
      pc.ondatachannel = (ev) => {
        const incomingDc = ev.channel;
        dcRef.current = incomingDc;
        addLog(
          `[RTC] ondatachannel fired: label="${incomingDc.label}", readyState=${incomingDc.readyState}`,
          'success'
        );
        console.log(`[WebRTC] ondatachannel: ${incomingDc.label} (state: ${incomingDc.readyState})`);
        setupDataChannelHandlers(incomingDc);
      };

      // Handle incoming media tracks (video from Vision renegotiation)
      pc.ontrack = (ev) => {
        console.log('[WebRTC] Received track:', ev.track.kind, ev.track.label);
        addLog(`Received ${ev.track.kind} track from peer`, 'success');

        if (ev.track.kind === 'video' && ev.streams.length > 0) {
          const stream = ev.streams[0];
          addLog(`Video stream available (stream id: ${stream.id})`, 'success');
          // Attach stream to video element if available
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            addLog(`Video stream attached to video element`, 'success');
          }
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (e) => {
        if (e.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const iceMsg = {
            type: 'ice',
            data: {
              sessionId: roomId,
              from: ownPeerId || 'unknown',
              to: offerFromPeerIdRef.current || targetPeerId || 'unknown',
              candidate: {
                candidate: e.candidate.candidate,
                sdpMid: e.candidate.sdpMid,
                sdpMLineIndex: e.candidate.sdpMLineIndex,
              }
            }
          };
          wsRef.current.send(JSON.stringify(iceMsg));
          iceCandidatesSentCountRef.current++;
          setStats((prev) => ({ ...prev, iceCandidatesSent: iceCandidatesSentCountRef.current }));
          addLog(`ICE candidate sent (total sent: ${iceCandidatesSentCountRef.current}): ${e.candidate.candidate.substring(0, 40)}...`, 'debug');
        } else if (!e.candidate) {
          addLog(`ICE gathering complete`, 'debug');
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        addLog(`RTC connection state: ${state}`, 'info');
        setStats((prev) => ({ ...prev, connectionStatus: state }));

        if (state === 'connected') {
          setRtcState('connected');
        } else if (state === 'failed' || state === 'disconnected') {
          setRtcState('answer-sent');
          addLog('RTC connection failed or disconnected', 'error');
        }
      };

      // Handle ICE connection state
      let lastIceState = 'new';
      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        addLog(`ICE connection state: ${lastIceState} → ${state}`, state === 'connected' ? 'success' : state === 'failed' ? 'error' : 'debug');
        lastIceState = state;
      };

      pcRef.current = pc;
      addLog('RTCPeerConnection created', 'success');
    } catch (e) {
      addLog(`Error setting up RTC connection: ${e}`, 'error');
      setError(`RTC setup failed: ${e}`);
      setConnectionState('error');
    }
  };


  const sendMessage = (msgObj: any) => {
    const dc = dcRef.current;
    if (!dc) {
      addLog(`[Send] No data channel (pcRef: ${pcRef.current ? 'exists' : 'null'}, dc: null)`, 'error');
      return;
    }

    if (dc.readyState !== 'open') {
      addLog(`[Send] Data channel not open (state: ${dc.readyState})`, 'error');
      return;
    }

    try {
      const json = JSON.stringify(msgObj);
      dc.send(json);
      setStats((prev) => ({ ...prev, messagesSent: prev.messagesSent + 1 }));
      addLog(`[Send] Message sent: ${msgObj.type}`, 'success');
    } catch (e) {
      addLog(`[Send] Error: ${e}`, 'error');
    }
  };

  const sendCommand = (command: string) => {
    sendMessage({
      type: 'command',
      command,
      value: null,
      timestamp: new Date().toISOString(),
    });
  };

  const connect = () => {
    if (!roomId.trim()) {
      setError('Room ID is required');
      return;
    }

    // Clean up old connections first (for reconnection)
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (dcRef.current) {
      dcRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setError('');
    setConnectionState('connecting');
    setRtcState('idle');
    // setLogs([]) - keep history across reconnections

    // Reset all state refs for new connection
    answerSentRef.current = false;
    bufferedIceCandidatesRef.current = [];
    offerCountRef.current = 0;
    answerCountRef.current = 0;
    iceCandidatesSentCountRef.current = 0;
    currentOfferIdRef.current = null;
    offerFromPeerIdRef.current = null;

    // Reset state variables
    setOwnPeerId(null);
    setTargetPeerId(null);

    setStats({
      offerReceived: false,
      answerSent: false,
      iceCandidatesSent: 0,
      iceCandidatesReceived: 0,
      dataChanelOpen: false,
      connectionStatus: 'Connecting',
      messagesSent: 0,
      messagesReceived: 0,
      lastHeartbeat: 0,
    });

    try {
      // Setup RTC connection first
      setupRTCConnection();

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${protocol}//${window.location.host}/webrtc/vision/${roomId}`;
      addLog(`Connecting to: ${url}`, 'info');

      const ws = new WebSocket(url);

      ws.onopen = () => {
        addLog('WebSocket connected', 'success');
        setConnectionState('connected');

        // Send join message with sessionId and role
        const joinMsg = {
          type: 'join',
          data: {
            sessionId: roomId,
            role: userRole,
          }
        };
        ws.send(JSON.stringify(joinMsg));
        addLog(`Sent join message with role=${userRole}`, 'info');
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onerror = (event) => {
        const errorMsg = `WebSocket error: ${event}`;
        addLog(errorMsg, 'error');
        setError(errorMsg);
        setConnectionState('error');
      };

      ws.onclose = () => {
        addLog('WebSocket closed', 'info');
        setConnectionState('disconnected');
        // Clean up RTC peer when WS closes unexpectedly
        if (pcRef.current) {
          pcRef.current.close();
          pcRef.current = null;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      };

      wsRef.current = ws;
    } catch (e) {
      const errorMsg = `Connection error: ${e}`;
      setError(errorMsg);
      setConnectionState('error');
      addLog(errorMsg, 'error');
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    setConnectionState('disconnected');
    setRtcState('idle');
    answerSentRef.current = false;
    bufferedIceCandidatesRef.current = [];
    addLog('Disconnected', 'info');
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLogs = () => {
    const logsText = logs
      .map((log) => `[${log.timestamp}] ${log.message}`)
      .join('\n');
    navigator.clipboard.writeText(logsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadLogs = () => {
    const logsText = logs
      .map((log) => `[${log.timestamp}] ${log.message}`)
      .join('\n');
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(logsText));
    element.setAttribute('download', `webrtc-controller-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'text-green-600';
      case 'connecting':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    switch (connectionState) {
      case 'connected':
        return <Wifi className="w-4 h-4" />;
      default:
        return <WifiOff className="w-4 h-4" />;
    }
  };

  const getLogColor = (level: string) => {
    switch (level) {
      case 'success':
        return 'text-green-700';
      case 'error':
        return 'text-red-700';
      case 'debug':
        return 'text-gray-500';
      default:
        return 'text-gray-700';
    }
  };

  return (
    <div className={`flex flex-col gap-4 p-4 h-full ${className}`}>
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold mb-2">WebRTC Auto Controller Client</h1>
        <p className="text-sm text-gray-600">
          Auto-joins room, handles offer/answer, manages ICE trickle, and opens data channel (event-driven)
        </p>
      </div>

      {/* Connection Section */}
      <Card className="p-4 bg-white border border-gray-200">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Room ID</label>
              <Input
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="Enter room UUID or name"
                disabled={connectionState === 'connected' || connectionState === 'connecting'}
                className="w-full"
              />
            </div>
            <Button
              onClick={copyRoomId}
              variant="outline"
              size="sm"
              className="mt-6"
              title="Copy room ID"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={connect}
              disabled={connectionState === 'connected' || connectionState === 'connecting'}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {connectionState === 'connecting' ? 'Connecting...' : 'Connect'}
            </Button>
            <Button
              onClick={disconnect}
              disabled={connectionState !== 'connected'}
              variant="destructive"
              className="flex-1"
            >
              Disconnect
            </Button>
          </div>

          <div className={`flex items-center gap-4 text-sm font-medium ${getStatusColor()}`}>
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <span>WS: {connectionState.charAt(0).toUpperCase() + connectionState.slice(1)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span>RTC: {rtcState}</span>
            </div>
            {connectionState === 'connected' && (
              <div className="flex items-center gap-1 ml-auto">
                <span className="text-xs text-gray-500">Heartbeat:</span>
                <div
                  className={`w-2 h-2 rounded-full ${
                    heartbeatActive ? 'bg-green-500 animate-pulse' : 'bg-gray-300'
                  }`}
                />
              </div>
            )}
          </div>

          {error && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Video Display Section */}
      {connectionState === 'connected' && (
        <Card className="p-4 bg-black border border-gray-200">
          <div className="flex flex-col gap-3">
            <h3 className="font-semibold text-sm text-white">Video Stream (Vision Peer)</h3>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full bg-black rounded-md border border-gray-700"
              style={{ maxHeight: '300px', objectFit: 'contain' }}
            />
            <p className="text-xs text-gray-400">
              {stats.dataChanelOpen ? '✓ Ready to receive video stream (click Start Stream)' : '⚠ Waiting for data channel...'}
            </p>
          </div>
        </Card>
      )}

      {/* Stats Section */}
      {connectionState === 'connected' && (
        <>
          <Card className="p-4 bg-white border border-gray-200">
            <h3 className="font-semibold text-sm mb-3">Connection Stats</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Offer Received</div>
                <div className={stats.offerReceived ? 'text-green-600 font-bold' : 'text-gray-400'}>
                  {stats.offerReceived ? '✓' : '✗'}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Answer Sent</div>
                <div className={stats.answerSent ? 'text-green-600 font-bold' : 'text-gray-400'}>
                  {stats.answerSent ? '✓' : '✗'}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Data Channel</div>
                <div className={stats.dataChanelOpen ? 'text-green-600 font-bold' : 'text-gray-400'}>
                  {stats.dataChanelOpen ? '✓' : '✗'}
                </div>
              </div>
              <div>
                <div className="text-gray-600">ICE Sent</div>
                <div className="text-blue-600 font-bold">{stats.iceCandidatesSent}</div>
              </div>
              <div>
                <div className="text-gray-600">ICE Received</div>
                <div className="text-blue-600 font-bold">{stats.iceCandidatesReceived}</div>
              </div>
              <div>
                <div className="text-gray-600">RTC Status</div>
                <div className="text-purple-600 font-bold text-xs">{stats.connectionStatus}</div>
              </div>
              <div>
                <div className="text-gray-600">Msgs Sent</div>
                <div className="text-orange-600 font-bold">{stats.messagesSent}</div>
              </div>
              <div>
                <div className="text-gray-600">Msgs Received</div>
                <div className="text-orange-600 font-bold">{stats.messagesReceived}</div>
              </div>
            </div>
          </Card>

          {/* Data Channel Testing */}
          <Card className="p-4 bg-white border border-gray-200">
            <h3 className="font-semibold text-sm mb-3">Data Channel Test</h3>
            <div className="space-y-2">
              <p className="text-xs text-gray-600">
                {dcRef.current && dcRef.current.readyState === 'open'
                  ? '✓ Data channel is open. Send test commands below.'
                  : '⚠ Data channel not open yet. Try sending when Vision connects.'}
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => sendCommand('ping')}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  Ping
                </Button>
                <Button
                  onClick={() => sendCommand('status')}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  Status
                </Button>
                <Button
                  onClick={() => sendCommand('start_stream')}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  Start Stream
                </Button>
                <Button
                  onClick={() => sendCommand('stop_stream')}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  Stop Stream
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Logs */}
      <Card className="flex-1 flex flex-col p-4 bg-white border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Event Log</h3>
          <div className="flex gap-2">
            <Button
              onClick={copyLogs}
              variant="outline"
              size="sm"
              disabled={logs.length === 0}
              title="Copy all logs to clipboard"
              className="text-xs h-7 px-2"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </Button>
            <Button
              onClick={downloadLogs}
              variant="outline"
              size="sm"
              disabled={logs.length === 0}
              title="Download logs as file"
              className="text-xs h-7 px-2"
            >
              <Download className="w-3 h-3" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-50 p-3 rounded border border-gray-200 font-mono text-xs space-y-1">
          {logs.length === 0 ? (
            <div className="text-gray-400">Logs will appear here...</div>
          ) : (
            logs.map((log, idx) => (
              <div key={idx} className={`${getLogColor(log.level)} break-words`}>
                <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {logs.length} log entries
        </div>
      </Card>

      {/* Info */}
      <Card className="p-3 bg-blue-50 border border-blue-200">
        <p className="text-xs text-blue-700">
          <strong>Auto-Controller:</strong> Connects to Vision, auto-joins room, handles offer/answer handshake,
          manages ICE trickle, and opens data channel. All event-driven (no manual buttons after connect).
        </p>
      </Card>
    </div>
  );
};
